/**
 * The color NAMES. Nothing else.
 *
 * A name becomes a color in CSS: put `data-color={color}` on the element and
 * use the static utilities `text-c`, `bg-c`, `bg-c-soft`, `text-c-foreground`,
 * `fill-c`, `stroke-c`. The `[data-color="…"]` rules in `tokens.css` bind the
 * name to its tokens, in both modes, live.
 *
 * Keep it that way: Tailwind's scanner only emits classes written out verbatim,
 * so a per-name class map cannot be generated from this list, and a JS resolver
 * cannot beat a var() the browser already re-resolves on its own.
 */

export const COLORS = [
  // Semantic
  "primary",
  // The never-neutral accent. `primary` follows the theme all the way to its
  // neutral extremes — white in the default dark theme, near-black in light —
  // so a mark that must stay a COLOR while still tracking the accent names
  // this one instead.
  "brand",
  "secondary",
  "destructive",
  "muted",
  "accent",
  "success",
  "warning",
  "info",
  // Palette — entities persist these names
  "dark-grey",
  "grey",
  "light-grey",
  "red",
  "orange",
  "yellow",
  "light-green",
  "green",
  "teal",
  "cyan",
  "light-blue",
  "blue",
  "purple",
  "light-purple",
  "violet",
  "magenta",
  "pink",
] as const;

export type Color = (typeof COLORS)[number];

const COLOR_SET: ReadonlySet<string> = new Set(COLORS);

/**
 * Whether an arbitrary string names a color on this scale.
 *
 * Persisted entity colors arrive as free-form strings because the column is a
 * string. This guard and {@link toColor} are the ONLY sanctioned way to turn
 * one into a {@link Color} — never `as Color`. Past that gate every prop,
 * field and variable that carries a color is typed `Color`, so a value the
 * scale does not know cannot reach a component that assumes it does.
 */
export function isColor(value: string | null | undefined): value is Color {
  return value != null && COLOR_SET.has(value);
}

/**
 * {@link isColor} in the shape a boundary wants: narrow a stored string to a
 * name on the scale, or `undefined` when it is not one.
 */
export function toColor(value: string | null | undefined): Color | undefined {
  return isColor(value) ? value : undefined;
}

/** Count of leading semantic entries in {@link COLORS}. */
const SEMANTIC_COUNT = 9;

/**
 * The palette subset — exactly the names entities persist. Derived from
 * {@link COLORS} so the two can never disagree.
 */
export const PALETTE_COLORS: readonly Color[] = COLORS.slice(SEMANTIC_COUNT);

/**
 * The colors pickers OFFER — curated membership and order: purple → blue →
 * cyan → teal → green → yellow → orange → red → pink → violet → dark-grey →
 * grey. The light/mid variants (light-grey, light-green, light-blue,
 * light-purple, magenta) are deliberately not offered — but they stay
 * renderable (see {@link PALETTE_COLORS}) so stored values keep their color.
 */
export const PICKER_COLORS: readonly Color[] = [
  "purple",
  "blue",
  "cyan",
  "teal",
  "green",
  "yellow",
  "orange",
  "red",
  "pink",
  "violet",
  "dark-grey",
  "grey",
];

/**
 * The greys read as "unset" on a solid tile, so a seeded-at-random color skips
 * them. They stay pickable — this only shapes what a fresh entity lands on.
 */
const NEUTRAL_PICKER_COLORS: readonly Color[] = ["dark-grey", "grey"];

/**
 * A random non-neutral entry from {@link PICKER_COLORS} — the seed for a
 * freshly created entity's tile, so consecutive creates do not all wear the
 * same color. Pass the previous pick as `exclude` so back-to-back creates
 * never land the same swatch twice.
 */
export function randomPickerColor(exclude?: Color): Color {
  const pool = PICKER_COLORS.filter(
    (c) => !NEUTRAL_PICKER_COLORS.includes(c) && c !== exclude,
  );
  // The fallback satisfies the indexed-access type only: the pool keeps at
  // least nine entries after both exclusions, so the index always lands.
  return pool[Math.floor(Math.random() * pool.length)] ?? "grey";
}

/**
 * The explicit "no color" choice — a picker selection, never a stored palette
 * name and never `null` (cleared is a different fact than never-picked).
 * Only pickers that opt in (`ColorPalette noColor`) offer or emit it.
 */
export const NO_COLOR = "none" as const;
export type NoColor = typeof NO_COLOR;

/** Human-readable name per color — swatch tooltips and accessible labels. */
export const COLOR_LABELS: Record<Color, string> = {
  primary: "Primary",
  brand: "Brand",
  secondary: "Secondary",
  destructive: "Destructive",
  muted: "Muted",
  accent: "Accent",
  success: "Success",
  warning: "Warning",
  info: "Info",
  "dark-grey": "Dark Grey",
  grey: "Grey",
  "light-grey": "Light Grey",
  red: "Red",
  orange: "Orange",
  yellow: "Yellow",
  "light-green": "Light Green",
  green: "Green",
  teal: "Teal",
  cyan: "Cyan",
  "light-blue": "Light Blue",
  blue: "Blue",
  purple: "Purple",
  "light-purple": "Light Purple",
  violet: "Violet",
  magenta: "Magenta",
  pink: "Pink",
};

/**
 * The label a swatch shows for `color` in `mode`.
 *
 * The greys are named for the light ground. `dark-grey` is the neutral that
 * contrasts the page, so its dark-mode fill is a LIGHT grey — the tooltip and
 * accessible name say what the viewer sees. Every other name reads the same
 * in both modes.
 */
export function colorLabel(color: Color, mode: "light" | "dark"): string {
  return color === "dark-grey" && mode === "dark"
    ? "Light Grey"
    : COLOR_LABELS[color];
}

/**
 * A `var()` reference to the token behind `color`, for the few places that take
 * a color as a STRING: a chart series accessor, an SVG `fill`/`stroke`/
 * `stop-color` prop on a component that forwards it as an attribute.
 *
 * `fill`, `stroke` and `stop-color` are CSS properties. They resolve `var()`
 * and `color-mix()` and they re-resolve on a theme flip, so a string built here
 * stays live — there is nothing to recompute in JS.
 *
 * The rules mirror `[data-color]` in `tokens.css`: `secondary`, `muted` and
 * `accent` are surface tokens, so as a mark color they hand back their foreground pair.
 */
export function colorVar(color: Color): string {
  if (color === "secondary") return "var(--secondary-foreground)";
  if (color === "muted") return "var(--muted-foreground)";
  if (color === "accent") return "var(--accent-foreground)";
  return `var(--${color})`;
}

/** {@link colorVar} at `alpha` opacity. */
export function colorVarAlpha(color: Color, alpha: number): string {
  return `color-mix(in oklab, ${colorVar(color)} ${alpha * 100}%, transparent)`;
}
